import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin, Flag, HeartHandshake, Mountain, Wrench } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/SectionHeading";
import { api } from "@/lib/api";
import { formatDateID, monthShortID, dayOf } from "@/lib/format";

const HERO_IMG = "/images/Captiva3.png";
const ABOUT_IMG_1 = "/images/Captiva15.png";
const ABOUT_IMG_2 = "/images/Captiva14.png";

const STATS = [
  { value: "2014", label: "Deklarasi Chapter" },
  { value: "14", label: "Chapter Nasional" },
  { value: "1.299+", label: "Anggota Nasional" },
  { value: "3", label: "Negara Touring" },
];

const VALUES = [
  { icon: HeartHandshake, title: "Persaudaraan", desc: "Satu Captiva, sejuta saudara. Silaturahmi antar anggota adalah prioritas utama chapter." },
  { icon: Mountain, title: "Petualangan", desc: "Touring Bromo, Pantura, hingga lintas negara. Jelajah Jawa Timur dan beyond." },
  { icon: Wrench, title: "Berbagi Ilmu", desc: "Sharing perawatan & modifikasi Captiva diesel maupun bensin dari sesama pemilik." },
  { icon: Flag, title: "Peduli Sosial", desc: "Bakti sosial rutin: dari santunan panti asuhan hingga bantuan untuk rumah sakit." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1 } }),
};

export default function Home() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }, [location]);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Navbar />

      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <img src={HERO_IMG} alt="Chevrolet Captiva malam hari" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0F]/50 via-[#090A0F]/60 to-[#090A0F]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F]/80 via-transparent to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-12">
          <motion.div initial="hidden" animate="show" className="max-w-3xl space-y-6">
            <motion.p variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-rose-400">
              Captiva Chevy Club — Chapter Jawa Timur
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1}
              className="font-heading text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Satu Captiva,
              <br />
              <span className="text-rose-500">Sejuta Saudara.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
              Komunitas pemilik Chevrolet Captiva di Jawa Timur. Dari kopdar di Surabaya, touring Bromo,
              sampai bakti sosial — kami jalan bareng sebagai keluarga.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4 pt-2">
              <Link to="/gabung" data-testid="hero-cta-pendaftaran-button"
                className="rounded-lg bg-rose-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-rose-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-rose-500 hover:shadow-rose-500/50">
                Gabung Komunitas
              </Link>
              <Link to="/event" data-testid="hero-cta-jelajah-event-button"
                className="rounded-lg border border-white/15 bg-slate-800/70 px-7 py-3.5 font-semibold text-slate-200 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-500/50 hover:text-amber-400">
                Lihat Agenda
              </Link>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 backdrop-blur-md sm:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\\s+/g, "-")}`} className="bg-[#11131F]/90 px-6 py-5">
                <p className="font-heading text-3xl font-bold text-amber-400 sm:text-4xl">{s.value}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-400">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-white/10 bg-[#0B0D15] py-3">
        <div className="flex w-max animate-marquee gap-10 font-heading text-lg font-semibold uppercase tracking-widest text-slate-500">
          {Array(2).fill(["Kopdar Surabaya", "Touring Bromo", "Bakti Sosial", "Modifikasi", "Silaturahmi", "Sejak 2014"]).flat().map((t, i) => (
            <span key={i} className="flex items-center gap-10">
              {t} <span className="text-rose-500">//</span>
            </span>
          ))}
        </div>
      </div>

      {/* PROFIL */}
      <section id="profil" className="scroll-mt-20 py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative lg:col-span-5">
            <img src={ABOUT_IMG_1} alt="Touring Captiva" className="w-full rounded-xl border border-white/10 object-cover shadow-2xl shadow-black/60" />
            &nbsp;
            &nbsp;
            &nbsp;
            &nbsp;
            &nbsp;
            <img src={ABOUT_IMG_2} alt="Night meet 3C Jatim" className="w-full rounded-xl border border-white/10 object-cover shadow-2xl shadow-black/60" />
            <div className="absolute -left-3 top-6 rounded-md bg-rose-600 px-4 py-2 font-heading text-xl font-bold uppercase text-white shadow-lg shadow-rose-600/40">
              Est. 2014
            </div>
          </motion.div>
          <div className="space-y-8 lg:col-span-7 lg:pl-6">
            <SectionHeading label="Profil Komunitas" title="Tentang 3C Chapter Jatim"
              desc="Berawal dari chapter Jateng-Jogja di awal 2014, 3C Chapter Jatim resmi dideklarasikan pada 31 Agustus 2014 dengan 114 anggota. Basecamp kami berada di Surabaya. Secara nasional, Captiva Chevy Club berdiri sejak 2009 dan kini menaungi 14 chapter di seluruh Indonesia — termasuk mencetak sejarah sebagai klub otomotif pertama di Indonesia yang touring tiga negara (Indonesia–Malaysia–Brunei) pada 2015." />
            <div className="grid gap-4 sm:grid-cols-2">
              {VALUES.map((v, i) => (
                <motion.div key={v.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                  data-testid={`value-card-${v.title.toLowerCase().replace(/\\s+/g, "-")}`}
                  className="rounded-xl border border-white/10 bg-[#12141C]/90 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-rose-500/30 hover:shadow-[0_0_25px_rgba(244,63,94,0.15)]">
                  <v.icon className="h-6 w-6 text-amber-400" />
                  <h3 className="mt-3 font-heading text-xl font-bold uppercase text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BERITA */}
      <section className="border-t border-white/5 bg-[#0B0D15] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-10 px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading label="Kabar Terbaru" title="Berita & Artikel" />
            <Link to="/berita" data-testid="home-all-news-link" className="group flex items-center gap-2 font-semibold text-rose-400 hover:text-rose-300">
              Semua Berita <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {news.map((n, i) => (
              <motion.article key={n.id} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                data-testid={`home-news-card-${n.id}`}
                className="group overflow-hidden rounded-xl border border-white/10 bg-[#12141C]/90 transition-all duration-300 hover:-translate-y-1 hover:border-rose-500/30">
                <Link to={`/berita/${n.id}`}>
                  <div className="h-44 overflow-hidden">
                    <img src={n.image_url} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="space-y-3 p-6">
                    <span className="inline-block rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 font-mono text-xs uppercase tracking-widest text-rose-400">{n.category}</span>
                    <h3 className="font-heading text-2xl font-bold leading-tight text-slate-100 group-hover:text-rose-400">{n.title}</h3>
                    <p className="line-clamp-2 text-sm text-slate-400">{n.excerpt}</p>
                    <p className="font-mono text-xs text-slate-500">{formatDateID(n.created_at)}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
            {news.length === 0 && <p className="text-slate-500">Memuat berita...</p>}
          </div>
        </div>
      </section>

      {/* EVENT */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-10 px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading label="Agenda Chapter" title="Event & Kopdar" desc="Jadwal kegiatan terdekat 3C Chapter Jatim. Semua anggota dan calon anggota dipersilakan hadir." />
            <Link to="/event" data-testid="home-all-events-link" className="group flex items-center gap-2 font-semibold text-rose-400 hover:text-rose-300">
              Semua Agenda <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="space-y-4">
            {events.map((e, i) => (
              <motion.div key={e.id} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                data-testid={`home-event-card-${e.id}`}
                className="flex flex-col gap-5 rounded-xl border border-white/10 bg-[#12141C]/90 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/40 sm:flex-row sm:items-center">
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-lg border border-amber-500/30 bg-amber-500/10 text-center">
                  <div>
                    <p className="font-heading text-3xl font-bold leading-none text-amber-400">{dayOf(e.date)}</p>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400/80">{monthShortID(e.date)}</p>
                  </div>
                </div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-heading text-2xl font-bold uppercase text-white">{e.title}</h3>
                    <span className="rounded-md border border-white/10 bg-slate-800/70 px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest text-slate-300">{e.category}</span>
                  </div>
                  <p className="line-clamp-1 text-sm text-slate-400">{e.description}</p>
                  <p className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-rose-500" />{e.location}</span>
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-rose-500" />{formatDateID(e.date)} • {e.time}</span>
                  </p>
                </div>
              </motion.div>
            ))}
            {events.length === 0 && <p className="text-slate-500">Memuat agenda...</p>}
          </div>
        </div>
      </section>

      {/* GALERI */}
      <section className="border-t border-white/5 bg-[#0B0D15] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-10 px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading label="Dokumentasi" title="Galeri Kegiatan" />
            <Link to="/galeri" data-testid="home-all-gallery-link" className="group flex items-center gap-2 font-semibold text-rose-400 hover:text-rose-300">
              Semua Foto <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {gallery.map((g, i) => (
              <motion.div key={g.id} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                data-testid={`home-gallery-item-${g.id}`}
                className={`group relative overflow-hidden rounded-xl border border-white/10 ${i === 0 ? "col-span-2 row-span-2 h-full min-h-[300px]" : "h-44 md:h-52"}`}>
                {g.media_type === "video" ? (
                  <video src={g.image_url} muted playsInline preload="metadata" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <img src={g.image_url} alt={g.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/80 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <p className="font-heading text-lg font-bold uppercase text-white">{g.title}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA GABUNG */}
      <section className="relative overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-950/40 via-[#090A0F] to-amber-950/30" />
        <div className="relative mx-auto max-w-4xl space-y-6 px-4 text-center sm:px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">Punya Captiva di Jawa Timur?</p>
            <h2 className="mt-3 font-heading text-4xl font-extrabold uppercase leading-none text-white sm:text-5xl lg:text-6xl">
              Saatnya Jadi Bagian dari <span className="text-rose-500">Keluarga 3C Jatim</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-300 md:text-lg">
              Isi formulir pendaftaran, pengurus chapter akan menghubungimu via WhatsApp untuk kopdar perdana.
            </p>
            <Link to="/gabung" data-testid="cta-section-register-button"
              className="mt-8 inline-block rounded-lg bg-amber-500 px-8 py-4 font-bold text-[#090A0F] shadow-lg shadow-amber-500/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-400 hover:shadow-amber-400/50">
              Isi Formulir Pendaftaran
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
